import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import {
  Shield,
  Settings,
  Users,
  Ban,
  AlertTriangle,
  Lock,
  Unlock,
  Trash2,
  Plus,
  RefreshCw,
  Clock,
  Globe,
  User,
  Save,
  FileText
} from 'lucide-react';

type TabType = 'settings' | 'users' | 'ip-bans' | 'login-attempts' | 'logs';


interface AdminUser {
  id: number;
  username: string;
  full_name: string;
  email: string;
  role: string;
  status: string;
  locked_at: string | null;
  locked_until: string | null;
  lock_reason: string | null;
  failed_attempts: number;
  is_locked: boolean;
}

interface IPBan {
  id: number;
  ip_address: string;
  reason: string;
  banned_by: string;
  banned_at: string;
  expires_at: string | null;
  is_permanent: boolean;
  status: string;
}

interface LoginAttempt {
  id: number;
  ip_address: string;
  username: string;
  attempted_at: string;
}

interface SecurityLog {
  id: number;
  event_type: string;
  syscall_name: string;
  identifier: string;
  details: Record<string, unknown>;
  created_at: string;
}

export default function Security() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<TabType>('settings');
  const [showBanModal, setShowBanModal] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [banForm, setBanForm] = useState({ ip_address: '', reason: '', duration: 1440, is_permanent: false });
  const [lockForm, setLockForm] = useState({ reason: '', duration: 0 });

  // Fetch security settings
  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ['security-settings'],
    queryFn: async () => {
      const response = await fetch('/api/v1/security/settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch settings');
      return response.json();
    },
    enabled: activeTab === 'settings'
  });

  // Fetch users
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['security-users'],
    queryFn: async () => {
      const response = await fetch('/api/v1/security/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      return response.json();
    },
    enabled: activeTab === 'users'
  });

  // Fetch IP bans
  const { data: bansData, isLoading: bansLoading } = useQuery({
    queryKey: ['security-ip-bans'],
    queryFn: async () => {
      const response = await fetch('/api/v1/security/ip-bans?status=all', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch IP bans');
      return response.json();
    },
    enabled: activeTab === 'ip-bans'
  });

  // Fetch login attempts
  const { data: attemptsData, isLoading: attemptsLoading } = useQuery({
    queryKey: ['security-login-attempts'],
    queryFn: async () => {
      const response = await fetch('/api/v1/security/login-attempts', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch login attempts');
      return response.json();
    },
    enabled: activeTab === 'login-attempts'
  });

  // Fetch security logs
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['security-logs'],
    queryFn: async () => {
      const response = await fetch('/api/v1/security/logs', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch security logs');
      return response.json();
    },
    enabled: activeTab === 'logs'
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: Record<string, unknown>) => {
      const response = await fetch('/api/v1/security/settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      if (!response.ok) throw new Error('Failed to update settings');
      return response.json();
    },
    onSuccess: () => {
      toast.success('Settings updated');
      queryClient.invalidateQueries({ queryKey: ['security-settings'] });
    },
    onError: () => toast.error('Failed to update settings')
  });

  // Lock user mutation
  const lockUserMutation = useMutation({
    mutationFn: async ({ userId, reason, duration }: { userId: number; reason: string; duration: number }) => {
      const response = await fetch(`/api/v1/security/users/${userId}/lock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason, duration })
      });
      if (!response.ok) throw new Error('Failed to lock user');
      return response.json();
    },
    onSuccess: () => {
      toast.success('User locked');
      queryClient.invalidateQueries({ queryKey: ['security-users'] });
      setShowLockModal(false);
    },
    onError: () => toast.error('Failed to lock user')
  });

  // Unlock user mutation
  const unlockUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      const response = await fetch(`/api/v1/security/users/${userId}/unlock`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to unlock user');
      return response.json();
    },
    onSuccess: () => {
      toast.success('User unlocked');
      queryClient.invalidateQueries({ queryKey: ['security-users'] });
    },
    onError: () => toast.error('Failed to unlock user')
  });

  // Reset attempts mutation
  const resetAttemptsMutation = useMutation({
    mutationFn: async (userId: number) => {
      const response = await fetch(`/api/v1/security/users/${userId}/reset-attempts`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to reset attempts');
      return response.json();
    },
    onSuccess: () => {
      toast.success('Failed attempts reset');
      queryClient.invalidateQueries({ queryKey: ['security-users'] });
    },
    onError: () => toast.error('Failed to reset attempts')
  });

  // Add IP ban mutation
  const addBanMutation = useMutation({
    mutationFn: async (ban: typeof banForm) => {
      const response = await fetch('/api/v1/security/ip-bans', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ban)
      });
      if (!response.ok) throw new Error('Failed to add IP ban');
      return response.json();
    },
    onSuccess: () => {
      toast.success('IP banned');
      queryClient.invalidateQueries({ queryKey: ['security-ip-bans'] });
      setShowBanModal(false);
      setBanForm({ ip_address: '', reason: '', duration: 1440, is_permanent: false });
    },
    onError: () => toast.error('Failed to ban IP')
  });

  // Remove IP ban mutation
  const removeBanMutation = useMutation({
    mutationFn: async (banId: number) => {
      const response = await fetch(`/api/v1/security/ip-bans/${banId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to remove ban');
      return response.json();
    },
    onSuccess: () => {
      toast.success('IP ban removed');
      queryClient.invalidateQueries({ queryKey: ['security-ip-bans'] });
    },
    onError: () => toast.error('Failed to remove ban')
  });

  // Clear login attempts mutation
  const clearAttemptsMutation = useMutation({
    mutationFn: async (ip?: string) => {
      const url = ip ? `/api/v1/security/login-attempts?ip=${ip}` : '/api/v1/security/login-attempts';
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to clear attempts');
      return response.json();
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['security-login-attempts'] });
    },
    onError: () => toast.error('Failed to clear attempts')
  });

  const tabs = [
    { id: 'settings' as TabType, label: 'Settings', icon: Settings },
    { id: 'users' as TabType, label: 'Users', icon: Users },
    { id: 'ip-bans' as TabType, label: 'IP Bans', icon: Ban },
    { id: 'login-attempts' as TabType, label: 'Login Attempts', icon: AlertTriangle },
    { id: 'logs' as TabType, label: 'Security Logs', icon: FileText },
  ];

  const [localSettings, setLocalSettings] = useState<Record<string, unknown>>({});

  const handleSettingChange = (key: string, value: unknown) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  const saveSettings = () => {
    if (Object.keys(localSettings).length > 0) {
      updateSettingsMutation.mutate(localSettings);
      setLocalSettings({});
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-red-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Security</h1>
            <p className="text-gray-500">Manage login settings, users, and IP bans</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold">Login & Security Settings</h2>
            <button
              onClick={saveSettings}
              disabled={Object.keys(localSettings).length === 0 || updateSettingsMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </div>

          {settingsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading settings...</div>
          ) : (
            <div className="space-y-6">
              {/* Login Settings */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-4">Login Protection</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Max Failed Attempts</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      defaultValue={settingsData?.settings?.login_max_attempts?.value ?? 5}
                      onChange={(e) => handleSettingChange('login_max_attempts', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Number of failed attempts before lockout</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Lockout Duration (minutes)</label>
                    <input
                      type="number"
                      min="1"
                      max="1440"
                      defaultValue={settingsData?.settings?.login_lockout_duration?.value ?? 15}
                      onChange={(e) => handleSettingChange('login_lockout_duration', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">How long to lock out after max attempts</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Attempt Window (minutes)</label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      defaultValue={settingsData?.settings?.login_attempt_window?.value ?? 15}
                      onChange={(e) => handleSettingChange('login_attempt_window', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Time window to count failed attempts</p>
                  </div>
                </div>
              </div>

              {/* IP Ban Settings */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-700 mb-4">Automatic IP Banning</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="ip_ban_enabled"
                      defaultChecked={settingsData?.settings?.ip_ban_enabled?.value ?? true}
                      onChange={(e) => handleSettingChange('ip_ban_enabled', e.target.checked)}
                      className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                    />
                    <label htmlFor="ip_ban_enabled" className="text-sm text-gray-600">
                      Enable automatic IP banning after repeated lockouts
                    </label>
                  </div>
                  <div></div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Ban Threshold (lockouts)</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      defaultValue={settingsData?.settings?.ip_ban_threshold?.value ?? 3}
                      onChange={(e) => handleSettingChange('ip_ban_threshold', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">Number of lockouts before auto IP ban</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Ban Duration (minutes)</label>
                    <input
                      type="number"
                      min="60"
                      max="43200"
                      defaultValue={settingsData?.settings?.ip_ban_duration?.value ?? 1440}
                      onChange={(e) => handleSettingChange('ip_ban_duration', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">1440 = 24 hours, 10080 = 7 days</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">Admin Users</h2>
          </div>
          
          {usersLoading ? (
            <div className="text-center py-8 text-gray-500">Loading users...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed Attempts</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lock Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {usersData?.users?.map((user: AdminUser) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-gray-900">{user.username}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                          user.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-medium ${user.failed_attempts > 0 ? 'text-orange-600' : 'text-gray-500'}`}>
                          {user.failed_attempts}
                        </span>
                        {user.failed_attempts > 0 && (
                          <button
                            onClick={() => resetAttemptsMutation.mutate(user.id)}
                            className="ml-2 text-xs text-blue-600 hover:underline"
                          >
                            Reset
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.is_locked ? (
                          <div>
                            <span className="flex items-center gap-1 text-red-600">
                              <Lock className="w-3 h-3" />
                              Locked
                            </span>
                            {user.lock_reason && (
                              <div className="text-xs text-gray-500">{user.lock_reason}</div>
                            )}
                          </div>
                        ) : (
                          <span className="flex items-center gap-1 text-green-600">
                            <Unlock className="w-3 h-3" />
                            Unlocked
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {user.is_locked ? (
                          <button
                            onClick={() => unlockUserMutation.mutate(user.id)}
                            className="text-green-600 hover:text-green-700"
                            title="Unlock user"
                          >
                            <Unlock className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowLockModal(true);
                            }}
                            className="text-red-600 hover:text-red-700"
                            title="Lock user"
                          >
                            <Lock className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* IP Bans Tab */}
      {activeTab === 'ip-bans' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">IP Bans</h2>
            <button
              onClick={() => setShowBanModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Plus className="w-4 h-4" />
              Add IP Ban
            </button>
          </div>
          
          {bansLoading ? (
            <div className="text-center py-8 text-gray-500">Loading IP bans...</div>
          ) : bansData?.bans?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No IP bans</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Banned By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Banned At</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bansData?.bans?.map((ban: IPBan) => (
                    <tr key={ban.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-gray-400" />
                          <span className="font-mono">{ban.ip_address}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{ban.reason}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{ban.banned_by}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(ban.banned_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {ban.is_permanent ? (
                          <span className="text-red-600 font-medium">Permanent</span>
                        ) : ban.expires_at ? (
                          new Date(ban.expires_at).toLocaleString()
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          ban.status === 'active' ? 'bg-red-100 text-red-700' :
                          ban.status === 'expired' ? 'bg-gray-100 text-gray-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {ban.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {ban.status === 'active' && (
                          <button
                            onClick={() => {
                              if (confirm('Remove this IP ban?')) {
                                removeBanMutation.mutate(ban.id);
                              }
                            }}
                            className="text-red-600 hover:text-red-700"
                            title="Remove ban"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Login Attempts Tab */}
      {activeTab === 'login-attempts' && (
        <div className="space-y-4">
          {/* Stats */}
          {attemptsData?.stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-2xl font-bold text-gray-900">{attemptsData.stats.total_attempts}</div>
                <div className="text-sm text-gray-500">Total Attempts</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-2xl font-bold text-gray-900">{attemptsData.stats.unique_ips}</div>
                <div className="text-sm text-gray-500">Unique IPs</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-2xl font-bold text-gray-900">{attemptsData.stats.unique_usernames}</div>
                <div className="text-sm text-gray-500">Unique Usernames</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-2xl font-bold text-orange-600">{attemptsData.stats.last_hour}</div>
                <div className="text-sm text-gray-500">Last Hour</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-2xl font-bold text-red-600">{attemptsData.stats.last_24h}</div>
                <div className="text-sm text-gray-500">Last 24 Hours</div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Recent Login Attempts</h2>
              <button
                onClick={() => {
                  if (confirm('Clear all login attempts?')) {
                    clearAttemptsMutation.mutate(undefined);
                  }
                }}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
                Clear All
              </button>
            </div>
            
            {attemptsLoading ? (
              <div className="text-center py-8 text-gray-500">Loading login attempts...</div>
            ) : attemptsData?.attempts?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No login attempts recorded</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {attemptsData?.attempts?.map((attempt: LoginAttempt) => (
                      <tr key={attempt.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-gray-400" />
                            <span className="font-mono">{attempt.ip_address}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span>{attempt.username || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            {new Date(attempt.attempted_at).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => {
                              setBanForm({ ...banForm, ip_address: attempt.ip_address, reason: 'Suspicious login attempts' });
                              setShowBanModal(true);
                            }}
                            className="text-red-600 hover:text-red-700 text-sm"
                            title="Ban this IP"
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Security Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="text-lg font-semibold">Security Logs</h2>
            <button
              onClick={() => queryClient.invalidateQueries({ queryKey: ['security-logs'] })}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 border rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
          
          {logsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading security logs...</div>
          ) : logsData?.logs?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No security logs</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Syscall</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Identifier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {logsData?.logs?.map((log: SecurityLog) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          log.event_type.includes('error') ? 'bg-red-100 text-red-700' :
                          log.event_type.includes('warning') ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {log.event_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{log.syscall_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">{log.identifier || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add IP Ban Modal */}
      {showBanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Add IP Ban</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">IP Address</label>
                <input
                  type="text"
                  value={banForm.ip_address}
                  onChange={(e) => setBanForm({ ...banForm, ip_address: e.target.value })}
                  placeholder="e.g., 192.168.1.100"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Reason</label>
                <input
                  type="text"
                  value={banForm.reason}
                  onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })}
                  placeholder="Reason for ban"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_permanent"
                  checked={banForm.is_permanent}
                  onChange={(e) => setBanForm({ ...banForm, is_permanent: e.target.checked })}
                  className="w-4 h-4 text-red-600 rounded"
                />
                <label htmlFor="is_permanent" className="text-sm text-gray-600">Permanent ban</label>
              </div>
              {!banForm.is_permanent && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    value={banForm.duration}
                    onChange={(e) => setBanForm({ ...banForm, duration: parseInt(e.target.value) })}
                    min="1"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">1440 = 24 hours</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowBanModal(false)}
                className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => addBanMutation.mutate(banForm)}
                disabled={!banForm.ip_address || addBanMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Ban IP
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lock User Modal */}
      {showLockModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Lock User: {selectedUser.username}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Reason</label>
                <input
                  type="text"
                  value={lockForm.reason}
                  onChange={(e) => setLockForm({ ...lockForm, reason: e.target.value })}
                  placeholder="Reason for locking"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Duration (minutes, 0 = indefinite)</label>
                <input
                  type="number"
                  value={lockForm.duration}
                  onChange={(e) => setLockForm({ ...lockForm, duration: parseInt(e.target.value) })}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowLockModal(false);
                  setSelectedUser(null);
                }}
                className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => lockUserMutation.mutate({
                  userId: selectedUser.id,
                  reason: lockForm.reason,
                  duration: lockForm.duration
                })}
                disabled={lockUserMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Lock User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
